import { Brand } from '@shared/layout';

/**
 * The two states a server-rendered gate never had to show.
 *
 * When the shell was a Server Component the session was already resolved before
 * any HTML existed, so there was no "checking…" and no way for the check itself
 * to fail. A static shell has both, and they must not be conflated: an employee
 * whose session is fine but whose API is unreachable should not be bounced to
 * Atelier to "sign in again" — that sends them round a loop that cannot fix
 * anything and hides the real outage.
 */

/** Shown while `GET /api/auth/me` is in flight. */
export function SessionPending() {
    return (
        <main
            className="flex min-h-screen items-center justify-center bg-surface-sunken px-6"
            aria-busy="true"
        >
            <div className="flex flex-col items-center gap-4">
                <Brand />
                <span
                    role="status"
                    className="size-5 animate-spin rounded-full border-2 border-line border-t-brand"
                >
                    <span className="sr-only">Checking your Atelier session…</span>
                </span>
            </div>
        </main>
    );
}

/** Shown when the session check failed for a reason that is not a 401. */
export function SessionUnavailable({ onRetry }: { onRetry: () => void }) {
    return (
        <main className="flex min-h-screen items-center justify-center bg-surface-sunken px-6">
            <div className="w-full max-w-md rounded-xl border border-line bg-surface p-8 shadow-card">
                <Brand />
                <h1 className="mt-7 text-xl font-semibold tracking-tight text-content">
                    Can&apos;t reach the Backoffice API
                </h1>
                <p className="mt-2 text-[13.5px] leading-relaxed text-content-muted">
                    Your Atelier session may well be fine — we just couldn&apos;t ask. This is
                    usually the API being down or still starting up, not a sign-in problem.
                </p>
                <button
                    type="button"
                    onClick={onRetry}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-[13.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-600"
                >
                    Try again
                </button>
                <p className="mt-5 border-t border-line-subtle pt-5 text-xs leading-relaxed text-content-faint">
                    Still failing? Check <code className="font-mono">/api/health</code> and ask in
                    #engineering.
                </p>
            </div>
        </main>
    );
}
